import { useEffect, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import { listActiveBatches } from '../api';
import { usePolling } from '../hooks/usePolling';
import type { BatchSummary } from '../types';

/**
 * Lightning-parity "Active batch runs" panel for the interior sub-app.
 *
 * Cloned 1:1 from ``web/src/components/BatchRunsTable.tsx``: same
 * progress bar, per-status pill summary, success-rate colouring and
 * "started X ago" formatting. The interior backend's
 * ``/api/interior/api/batches`` already returns the
 * ``completion_percentage`` + ``success_percentage`` fields lightning's
 * table expects (see :func:`interior.jobs.JobStore.batch_summary`), so
 * the only deltas vs the lightning copy are the import paths and the
 * use of interior's ``usePolling`` (which returns ``{data,refresh}``
 * instead of an ``onSuccess`` callback).
 */

const columnHelper = createColumnHelper<BatchSummary>();

function ProgressBar({ percentage }: { percentage: number }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm font-medium text-slate-700 min-w-[3rem] text-right tabular-nums">
        {percentage.toFixed(0)}%
      </span>
    </div>
  );
}

function StatusSummary({
  completed,
  failed,
  inProgress,
  pending,
}: {
  completed: number;
  failed: number;
  inProgress: number;
  pending: number;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {completed > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-success-50 text-success-700 font-medium">
          {completed} done
        </span>
      )}
      {failed > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-danger-50 text-danger-700 font-medium">
          {failed} failed
        </span>
      )}
      {inProgress > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-warning-50 text-warning-700 font-medium animate-pulse">
          {inProgress} running
        </span>
      )}
      {pending > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium">
          {pending} queued
        </span>
      )}
    </div>
  );
}

const columns = [
  columnHelper.accessor('filename', {
    header: 'CSV File',
    cell: (info) => (
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="font-medium text-slate-900">{info.getValue()}</span>
      </div>
    ),
  }),
  columnHelper.accessor('total_scans', {
    header: 'Total Scans',
    cell: (info) => <span className="font-mono text-slate-700">{info.getValue()}</span>,
  }),
  columnHelper.accessor('completion_percentage', {
    header: 'Progress',
    cell: (info) => <ProgressBar percentage={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: (info) => (
      <StatusSummary
        completed={info.row.original.completed}
        failed={info.row.original.failed}
        inProgress={info.row.original.in_progress}
        pending={info.row.original.pending}
      />
    ),
  }),
  columnHelper.accessor('success_percentage', {
    header: 'Success Rate',
    cell: (info) => {
      const value = info.getValue();
      const total = info.row.original.completed + info.row.original.failed;
      if (total === 0) {
        return <span className="text-slate-400">-</span>;
      }
      return (
        <span
          className={`font-mono font-medium tabular-nums ${value >= 90 ? 'text-success-700' : value >= 70 ? 'text-warning-700' : 'text-danger-700'}`}
        >
          {value.toFixed(0)}%
        </span>
      );
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Started',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '-';
      try {
        return (
          <span className="text-slate-500 text-sm">
            {formatDistanceToNow(new Date(value), { addSuffix: true })}
          </span>
        );
      } catch {
        return '-';
      }
    },
  }),
];

export function BatchRunsTable() {
  const { data, loading, refresh } = usePolling(listActiveBatches, 3000);
  const batches = data ?? [];
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    if (data !== null) {
      setLastUpdated(new Date());
    }
  }, [data]);

  const table = useReactTable({
    data: batches,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!loading && batches.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
            <h2 className="text-base font-semibold text-slate-900">Active Batch Runs</h2>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            {batches.length} batch{batches.length !== 1 ? 'es' : ''} in progress
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-500">
              Updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}
            </span>
          )}
          <button
            onClick={refresh}
            className="btn-secondary px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {loading && data === null ? (
        <div className="p-12 text-center">
          <div className="inline-flex items-center gap-3 text-slate-500">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading batch runs...
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className="px-5 py-3 text-left">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-5 py-3"
                      data-label={typeof cell.column.columnDef.header === 'string' ? cell.column.columnDef.header : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-5 py-3 border-t border-slate-100">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Auto-refreshing every 3 seconds</span>
          <span>Batches with all jobs completed are hidden</span>
        </div>
      </div>
    </div>
  );
}
